import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import styled, { useTheme } from 'styled-components'
import { ProductsListItemResponseDto } from '@/apis/rest/generated/types'
import { Typo, TypoVariant } from '@/components/common/typography'
import { ScrollContainer } from '@/components/domains/etc'
import { useRecentlyProductList } from '@/components/domains/mypage/favorites/hooks/use-recently-product-list'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import { PRODUCT_CARD_SIZES } from '@/components/domains/products/new-product-card/new-product-card.type'
import { PageSizeEnum } from '@/constants/page-size.enum'
import { useProductDetailEventTracker } from '@/containers/event-tracker/product-detail.event-tracker-new'
import { transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'
import { encodeId } from '@/utils/utilApi'

const RecentlyProductListTitle = '최근 본 상품'

interface ProductDetailRecentlyProductListProps {
  discountRate: number
  productId: number
  isRealSoldOut: boolean
  productName: string
  price: number
  salePrice: number
  reviewScore: number
  reviewCount: number
}

export const ProductDetailRecentlyProductList = ({
  discountRate,
  productId,
  isRealSoldOut,
  productName,
  price,
  salePrice,
  reviewScore,
  reviewCount,
}: ProductDetailRecentlyProductListProps) => {
  const { trackViewProductDetailBottomProductSecond, trackClickProductDetailBottomProduct } =
    useProductDetailEventTracker()
  const { ref, inView: isInView } = useInView({
    triggerOnce: true,
  })

  const { color } = useTheme()

  const { recentlyProducts } = useRecentlyProductList({
    length: PageSizeEnum.PageSize20,
    currentProductId: encodeId('ProductType', productId),
  })

  useEffect(() => {
    if (isInView) {
      trackViewProductDetailBottomProductSecond({
        listType: 'recentlyproduct',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView])

  const handleClickProductCard = ({ item, listIndex }: { item: ProductsListItemResponseDto; listIndex: number }) => {
    trackClickProductDetailBottomProduct({
      discountRate,
      productId,
      isRealSoldOut,
      productName,
      price,
      salePrice,
      reviewScore,
      reviewCount,
      listType: 'recentproduct',
      listIndex,
      clickedProduct: {
        discountRate: item.discountRate || 0,
        productId: item.id,
        isRealSoldOut: item.isRealSoldOut,
        productName: item.name,
        price: item.price,
        salePrice: item.salePrice,
        reviewScore: item.review.score,
        reviewCount: item.review.count,
      },
    })
  }

  if (!recentlyProducts?.length) {
    return null
  }

  return (
    <ProductDetailRecentlyProductListWrapper className="product-detail-recently-product-list" ref={ref}>
      <div className="product-detail-recently-product-list__header">
        <Typo variant={TypoVariant.Heading6Bold} color={color.gray900}>
          {RecentlyProductListTitle}
        </Typo>
      </div>
      <ScrollContainer hideRightButton={recentlyProducts.length < 3} buttonPositionY={30}>
        <div className="product-detail-recently-product-list__list">
          {recentlyProducts.map((product, index) => {
            const productData = transformProductTypeDefinedByFrontend(product)
            return (
              <div className="product-detail-recently-product-list__item" key={productData.id}>
                <NewProductCard
                  product={productData}
                  size={PRODUCT_CARD_SIZES.lg}
                  onClickProductCard={() => handleClickProductCard({ item: product, listIndex: index })}
                />
              </div>
            )
          })}
        </div>
      </ScrollContainer>
    </ProductDetailRecentlyProductListWrapper>
  )
}
const ProductDetailRecentlyProductListWrapper = styled.div`
  padding: 1.6rem 0 0 0;
  .product-detail-recently-product-list {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 1.6rem;
    }
    &__list {
      margin-top: 1.6rem;
      display: grid;
      grid-auto-flow: column;
      grid-template-columns: repeat(3, 1fr);
      grid-column-gap: 0.8rem;
      & > *:first-child {
        padding-left: 1.6rem;
      }
      & > *:last-child {
        padding-right: 1.6rem;
      }
    }
  }
`
